<template>
  <div class="amp-device">
    <!-- Apple Maps Top Header bar -->
    <div class="amp-header-bar">
      <div class="amp-search-box amp-clickable" @click="openAppleMaps" :title="$t('dash.presenceManagement.apple.openInMaps')">
        <i class="material-icons amp-search-icon">search</i>
        <span class="amp-search-text">{{ name || $t('dash.presenceManagement.apple.searchPlaceholder') }}</span>
      </div>
      <div class="amp-header-actions">
        <button class="amp-header-btn" @click="sharePage" :title="$t('dash.presenceManagement.mockup.share')">
          <i class="material-icons">ios_share</i>
        </button>
      </div>
    </div>

    <!-- Phone screen / Apple Maps Place Card -->
    <div class="amp-screen">

      <!-- Cover Photo Banner with subtle iOS gradient -->
      <div 
        class="amp-cover amp-clickable"
        :style="coverImageUrl ? `background-image: url('${coverImageUrl}')` : ''"
        @click="openAppleMaps"
        :title="$t('dash.presenceManagement.apple.openInMaps')"
      >
        <div v-if="!coverImageUrl" class="amp-cover-placeholder">
          <i class="material-icons">map</i>
        </div>
        <div class="amp-cover-gradient"></div>

        <!-- Apple Maps Category Pin on Top of Cover -->
        <div class="amp-pin-badge">
          <i class="material-icons">place</i>
        </div>
      </div>

      <!-- Main Place Card Section -->
      <div class="amp-card">

        <!-- Place Identity Header -->
        <div class="amp-identity">
          <div class="amp-avatar-wrapper" v-if="imageUrl">
            <img :src="imageUrl" :alt="name" class="amp-avatar-img" />
          </div>
          <div class="amp-title-wrapper">
            <h4 class="amp-place-name amp-clickable" @click="openAppleMaps" :title="$t('dash.presenceManagement.apple.openInMaps')">
              {{ name || $t('dash.presenceManagement.apple.defaultPlaceName') }}
            </h4>
            <div class="amp-meta-row">
              <span class="amp-category-badge">{{ categoryName || $t('dash.presenceManagement.apple.defaultCategory') }}</span>
              <span class="amp-dot-sep">•</span>
              <span class="amp-status-open">{{ $t('dash.presenceManagement.mockup.currentlyOpen') }}</span>
            </div>
          </div>
        </div>

        <!-- Apple Action Buttons Row (iOS Blue Buttons) -->
        <div class="amp-actions-row">
          <button class="amp-action-btn amp-action-btn--primary" @click="openDirections" :title="$t('dash.presenceManagement.location.profile.preview.directions')">
            <i class="material-icons">directions</i>
            <span>{{ $t('dash.presenceManagement.apple.actions.directions') }}</span>
          </button>

          <button class="amp-action-btn" v-if="phone" @click="callPhone" :title="$t('dash.presenceManagement.location.profile.preview.call')">
            <i class="material-icons">call</i>
            <span>{{ $t('dash.presenceManagement.apple.actions.call') }}</span>
          </button>

          <button class="amp-action-btn" v-if="website" @click="openWebsite" :title="$t('dash.presenceManagement.location.profile.preview.website')">
            <i class="material-icons">language</i>
            <span>{{ $t('dash.presenceManagement.apple.actions.website') }}</span>
          </button>

          <button class="amp-action-btn" @click="sharePage" :title="$t('dash.presenceManagement.mockup.share')">
            <i class="material-icons">share</i>
            <span>{{ $t('dash.presenceManagement.apple.actions.share') }}</span>
          </button>
        </div>

        <!-- Apple iOS Inset Grouped Details Card -->
        <div class="amp-details-card">
          <div class="amp-detail-row" v-if="address">
            <div class="amp-detail-icon-wrap">
              <i class="material-icons">location_on</i>
            </div>
            <div class="amp-detail-content">
              <p class="amp-detail-title">{{ $t('dash.presenceManagement.apple.labels.address') }}</p>
              <p class="amp-detail-value">{{ address }}</p>
            </div>
          </div>

          <div class="amp-detail-row" v-if="phone">
            <div class="amp-detail-icon-wrap">
              <i class="material-icons">phone</i>
            </div>
            <div class="amp-detail-content amp-clickable" @click="callPhone">
              <p class="amp-detail-title">{{ $t('dash.presenceManagement.apple.labels.phone') }}</p>
              <p class="amp-detail-value amp-link">{{ phone }}</p>
            </div>
          </div>

          <div class="amp-detail-row" v-if="website">
            <div class="amp-detail-icon-wrap">
              <i class="material-icons">public</i>
            </div>
            <div class="amp-detail-content amp-clickable" @click="openWebsite">
              <p class="amp-detail-title">{{ $t('dash.presenceManagement.apple.labels.website') }}</p>
              <p class="amp-detail-value amp-link">{{ cleanWebsite }}</p>
            </div>
          </div>

          <div class="amp-detail-row" v-if="description">
            <div class="amp-detail-icon-wrap">
              <i class="material-icons">notes</i>
            </div>
            <div class="amp-detail-content">
              <p class="amp-detail-title">{{ $t('dash.presenceManagement.apple.labels.about') }}</p>
              <p class="amp-detail-value amp-description-text">{{ description }}</p>
            </div>
          </div>
        </div>

        <!-- Photos Carousel Strip -->
        <div class="amp-photos-section" v-if="allPhotos.length > 0">
          <div class="amp-photos-header">
            <span class="amp-photos-title">{{ $t('dash.presenceManagement.apple.labels.photos') }}</span>
            <span class="amp-photos-count">{{ allPhotos.length }}</span>
          </div>
          <div class="amp-photos-scroll">
            <div 
              v-for="(photo, index) in allPhotos" 
              :key="index" 
              class="amp-photo-thumb"
              :style="`background-image: url('${photo}')`"
            ></div>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'apple-mockup-preview',

  props: {
    name: { type: String, default: '' },
    address: { type: String, default: '' },
    phone: { type: String, default: '' },
    website: { type: String, default: '' },
    categoryName: { type: String, default: '' },
    imageUrl: { type: String, default: '' },
    coverImageUrl: { type: String, default: '' },
    description: { type: String, default: '' },
    mapsUri: { type: String, default: '' },
    additionalPhotos: { type: Array, default: () => [] }
  },

  computed: {
    cleanWebsite() {
      if (!this.website) return ''
      return this.website.replace(/^https?:\/\//, '').replace(/\/$/, '')
    },

    allPhotos() {
      const photos = []
      if (this.coverImageUrl) photos.push(this.coverImageUrl)
      if (this.imageUrl) photos.push(this.imageUrl)
      if (Array.isArray(this.additionalPhotos)) {
        photos.push(...this.additionalPhotos)
      }
      return photos
    }
  },

  methods: {
    openAppleMaps() {
      if (this.mapsUri) {
        window.open(this.mapsUri, '_blank')
      } else if (this.name || this.address) {
        const query = encodeURIComponent(`${this.name} ${this.address}`.trim())
        window.open(`https://maps.apple.com/?q=${query}`, '_blank')
      }
    },

    openDirections() {
      if (this.address) {
        const query = encodeURIComponent(this.address)
        window.open(`https://maps.apple.com/?daddr=${query}`, '_blank')
      } else {
        this.openAppleMaps()
      }
    },

    callPhone() {
      if (this.phone) {
        window.location.href = `tel:${this.phone}`
      }
    },

    openWebsite() {
      if (this.website) {
        const url = this.website.startsWith('http') ? this.website : `https://${this.website}`
        window.open(url, '_blank')
      }
    },

    sharePage() {
      if (navigator.share) {
        navigator.share({
          title: this.name,
          url: this.mapsUri || window.location.href
        }).catch(() => {})
      } else {
        this.openAppleMaps()
      }
    }
  }
}
</script>

<style scoped>
.amp-device {
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 8px solid #1c1c1e;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Helvetica Neue', sans-serif;
  color: #1c1c1e;
}

/* Header bar */
.amp-header-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px 8px;
  background: #f2f2f7;
  border-bottom: 1px solid #e5e5ea;
}

.amp-search-box {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  padding: 7px 12px;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.amp-search-icon {
  font-size: 17px;
  color: #8e8e93;
}

.amp-search-text {
  font-size: 13px;
  font-weight: 400;
  color: #1c1c1e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.amp-header-btn {
  background: #ffffff;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  color: #007aff;
}

.amp-header-btn i {
  font-size: 17px;
}

/* Screen */
.amp-screen {
  background: #f2f2f7;
  min-height: 520px;
  display: flex;
  flex-direction: column;
}

/* Cover */
.amp-cover {
  position: relative;
  width: 100%;
  height: 140px;
  background: #d1d1d6 center/cover no-repeat;
  cursor: pointer;
}

.amp-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8e8e93;
}

.amp-cover-placeholder i {
  font-size: 48px;
}

.amp-cover-gradient {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50px;
  background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.35));
}

.amp-pin-badge {
  position: absolute;
  bottom: 10px;
  right: 12px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #ff3b30;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

.amp-pin-badge i {
  font-size: 20px;
}

/* Place Card */
.amp-card {
  padding: 14px 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.amp-identity {
  display: flex;
  align-items: center;
  gap: 12px;
}

.amp-avatar-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e5ea;
}

.amp-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.amp-title-wrapper {
  flex: 1;
  min-width: 0;
}

.amp-place-name {
  font-size: 17px;
  font-weight: 700;
  color: #1c1c1e;
  margin: 0 0 3px;
  line-height: 1.25;
}

.amp-meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #8e8e93;
}

.amp-category-badge {
  color: #8e8e93;
  font-weight: 500;
}

.amp-dot-sep {
  font-size: 10px;
}

.amp-status-open {
  color: #34c759;
  font-weight: 600;
}

/* Action Buttons Row */
.amp-actions-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.amp-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 8px 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: #007aff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.amp-action-btn i {
  font-size: 20px;
}

.amp-action-btn span {
  font-size: 10.5px;
  font-weight: 600;
}

.amp-action-btn:hover {
  background: #e5e5ea;
}

.amp-action-btn--primary {
  background: #007aff;
  color: #ffffff;
}

.amp-action-btn--primary:hover {
  background: #0062cc;
}

/* Inset Grouped Details Card */
.amp-details-card {
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.amp-detail-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 11px 14px;
  border-bottom: 1px solid #f2f2f7;
}

.amp-detail-row:last-child {
  border-bottom: none;
}

.amp-detail-icon-wrap {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #007aff;
  flex-shrink: 0;
  margin-top: 1px;
}

.amp-detail-icon-wrap i {
  font-size: 18px;
}

.amp-detail-content {
  flex: 1;
  min-width: 0;
}

.amp-detail-title {
  font-size: 11px;
  font-weight: 500;
  color: #8e8e93;
  margin: 0 0 2px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.amp-detail-value {
  font-size: 13px;
  font-weight: 400;
  color: #1c1c1e;
  margin: 0;
  line-height: 1.35;
  word-break: break-word;
}

.amp-link {
  color: #007aff;
  cursor: pointer;
}

.amp-link:hover {
  text-decoration: underline;
}

.amp-description-text {
  color: #3a3a3c;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Photos Section */
.amp-photos-section {
  background: #ffffff;
  border-radius: 14px;
  padding: 12px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.amp-photos-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.amp-photos-title {
  font-size: 13px;
  font-weight: 600;
  color: #1c1c1e;
}

.amp-photos-count {
  font-size: 11px;
  font-weight: 600;
  color: #8e8e93;
  background: #f2f2f7;
  padding: 2px 7px;
  border-radius: 10px;
}

.amp-photos-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.amp-photo-thumb {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  background: #e5e5ea center/cover no-repeat;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.amp-clickable {
  cursor: pointer;
}
</style>
